import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Menu, X, ChevronRight, Phone, Mail, MapPin, Linkedin, Send, CheckCircle, ArrowRight } from 'lucide-react';
import './App.css';
import LiveChatWidget from './LiveChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LINKEDIN_URL = 'https://www.linkedin.com/company/zenom-technologies-ltd/home/?viewAsMember=true';
const TELECOM_TOWER_IMAGE = 'https://images.unsplash.com/photo-1602823284936-463177448097?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85';
const NETWORK_DIAGRAM_IMAGE = 'https://images.unsplash.com/photo-1664526937033-fe2c11f1be25?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85';
const DATA_CENTER_IMAGE = 'https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service_interest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);

  useEffect(() => {
    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        service_interest: ''
      });
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus(null);

    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterStatus('error');
      console.error('Error subscribing to newsletter:', error);
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="https://static.wixstatic.com/media/022cd5_82670ae94fbc4e3b809e81767dc7ea23~mv2.jpg/v1/fill/w_210,h_210,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-04-02%20at%2018_50_edited.jpg" 
                alt="ZenomTech Logo" 
                className="h-10 w-10 rounded-full"
              />
              <span className="ml-3 text-xl font-bold text-blue-900">ZenomTech</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="nav-link" data-testid="desktop-nav-home-button">Home</button>
              <button onClick={() => scrollToSection('services')} className="nav-link" data-testid="desktop-nav-services-button">Services</button>
              <button onClick={() => scrollToSection('ai-capabilities')} className="nav-link" data-testid="desktop-nav-ai-capabilities-button">AI Capabilities</button>
              <button onClick={() => scrollToSection('why-us')} className="nav-link" data-testid="desktop-nav-why-us-button">Why Us</button>
              <button onClick={() => scrollToSection('customer-experience')} className="nav-link" data-testid="desktop-nav-customer-experience-button">Experience</button>
              <button onClick={() => scrollToSection('case-studies')} className="nav-link" data-testid="desktop-nav-case-studies-button">Case Studies</button>
              <button onClick={() => scrollToSection('insights')} className="nav-link" data-testid="desktop-nav-insights-button">Insights</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link" data-testid="desktop-nav-contact-button">Contact</button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-700" data-testid="mobile-menu-toggle-button">
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden pb-4">
              <button onClick={() => scrollToSection('home')} className="mobile-nav-link" data-testid="mobile-nav-home-button">Home</button>
              <button onClick={() => scrollToSection('services')} className="mobile-nav-link" data-testid="mobile-nav-services-button">Services</button>
              <button onClick={() => scrollToSection('ai-capabilities')} className="mobile-nav-link" data-testid="mobile-nav-ai-capabilities-button">AI Capabilities</button>
              <button onClick={() => scrollToSection('why-us')} className="mobile-nav-link" data-testid="mobile-nav-why-us-button">Why Us</button>
              <button onClick={() => scrollToSection('customer-experience')} className="mobile-nav-link" data-testid="mobile-nav-customer-experience-button">Experience</button>
              <button onClick={() => scrollToSection('case-studies')} className="mobile-nav-link" data-testid="mobile-nav-case-studies-button">Case Studies</button>
              <button onClick={() => scrollToSection('insights')} className="mobile-nav-link" data-testid="mobile-nav-insights-button">Insights</button>
              <button onClick={() => scrollToSection('contact')} className="mobile-nav-link" data-testid="mobile-nav-contact-button">Contact</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">
            Redefining BSS/OSS Solutions
          </h1>
          <p className="hero-subtitle">
            20+ Years of Excellence in Telecom & IT Consulting
          </p>
          <p className="hero-description">
            Trusted by Tier 1-2-3 operators across Americas, EMEA, and APAC
          </p>
          <button 
            onClick={() => scrollToSection('contact')} 
            className="hero-cta"
            data-testid="hero-cta-button"
          >
            Get Started <ChevronRight className="ml-2" size={20} />
          </button>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="services-title">Our Services</h2>
            <p className="section-subtitle">Comprehensive IT & Telecom Solutions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="service-card" data-testid="service-bss-oss">
              <div className="service-icon bg-blue-100">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">BSS/OSS Transformation</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Enterprise Architecture Consulting</li>
                <li>• Digital Roadmap & Rollouts</li>
                <li>• MNO & MVNO Deployments</li>
                <li>• TM Forum Business Processes</li>
                <li>• RFP Evaluations</li>
              </ul>
            </div>

            {/* Service 2 */}
            <div className="service-card" data-testid="service-data-analytics">
              <div className="service-icon bg-blue-100">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Analytics</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Customer Segmentation</li>
                <li>• Business Intelligence</li>
                <li>• Dashboard Reporting</li>
                <li>• Customer Value Management</li>
                <li>• Predictive Analytics</li>
              </ul>
            </div>

            {/* Service 3 */}
            <div className="service-card" data-testid="service-enterprise-it">
              <div className="service-icon bg-blue-100">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Enterprise IT Services & TM Forum</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Digital Transformation</li>
                <li>• ERP / CRM Integration</li>
                <li>• TM Forum Open APIs</li>
                <li>• Business Process Framework</li>
                <li>• 5G & IoT Solutions</li>
                <li>• End-to-End Solution Delivery</li>
              </ul>
            </div>

            {/* Service 4 */}
            <div className="service-card" data-testid="service-cloud">
              <div className="service-icon bg-blue-100">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cloud Solutions</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Cloud Architecture Design</li>
                <li>• Cloud Spend Optimization</li>
                <li>• GCP, AWS, Azure Expertise</li>
                <li>• VMware Tanzu</li>
                <li>• Migration Services</li>
              </ul>
            </div>

            {/* Service 5 */}
            <div className="service-card" data-testid="service-integration">
              <div className="service-icon bg-blue-100">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">System Integration</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• 3rd Party Integration</li>
                <li>• Testing Automation</li>
                <li>• Remote Testing Services</li>
                <li>• API Development</li>
                <li>• Legacy System Integration</li>
              </ul>
            </div>

            {/* Service 6 - AI Tech */}
            <div className="service-card" data-testid="service-ai-tech">
              <div className="service-icon bg-blue-100">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">AI Technology Solutions</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Semantic API Layer Creation</li>
                <li>• Telecom Ontology Development</li>
                <li>• AI-Powered BSS/OSS Integration</li>
                <li>• Knowledge Graph Design</li>
                <li>• Intelligent Data Modeling</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Focus Graphics */}
      <section id="technology-focus" className="technology-focus-section" data-testid="technology-focus-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="technology-focus-heading">
            <h2 className="section-title" data-testid="technology-focus-title">Telecom technology in motion</h2>
            <p className="section-subtitle" data-testid="technology-focus-subtitle">
              Practical visuals around the systems ZenomTech helps modernize, connect, and accelerate.
            </p>
          </div>

          <div className="technology-visual-grid">
            <article className="technology-visual-card tall" data-testid="technology-visual-bss-card">
              <img src={TELECOM_TOWER_IMAGE} alt="Telecom tower infrastructure" className="technology-visual-image" data-testid="technology-visual-bss-image" />
              <div className="technology-visual-overlay">
                <span>Carrier Infrastructure</span>
                <h3>Modernizing BSS/OSS for network-scale operations</h3>
              </div>
            </article>

            <article className="technology-visual-card" data-testid="technology-visual-ai-card">
              <img src={NETWORK_DIAGRAM_IMAGE} alt="AI semantic network diagram" className="technology-visual-image" data-testid="technology-visual-ai-image" />
              <div className="technology-visual-overlay">
                <span>AI Semantic Layer</span>
                <h3>Connecting legacy systems to business-ready intelligence</h3>
              </div>
            </article>

            <article className="technology-visual-card" data-testid="technology-visual-cloud-card">
              <img src={DATA_CENTER_IMAGE} alt="Modern data center server racks" className="technology-visual-image" data-testid="technology-visual-cloud-image" />
              <div className="technology-visual-overlay">
                <span>Cloud & Integration</span>
                <h3>Building resilient platforms for faster product launches</h3>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* AI Capabilities Section */}
      <section id="ai-capabilities" className="ai-capabilities-section section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="ai-capabilities-grid">
            <div className="ai-capabilities-copy">
              <div className="ai-eyebrow" data-testid="ai-capabilities-eyebrow">AI Capabilities</div>
              <h2 className="ai-capabilities-title" data-testid="ai-capabilities-title">
                AI tools built for legacy carrier BSS environments
              </h2>
              <p className="ai-capabilities-description" data-testid="ai-capabilities-description">
                ZenomTech is building AI capabilities that can speak to carriers’ legacy BSS platforms, connect fragmented data and process layers, and unlock greater business value by helping teams launch new products faster in the market.
              </p>

              <div className="ai-value-list" data-testid="ai-capabilities-value-list">
                <div className="ai-value-item" data-testid="ai-value-legacy-bss">
                  <span className="ai-value-number">01</span>
                  <div>
                    <h3>Legacy BSS understanding</h3>
                    <p>AI-assisted interpretation of product, customer, billing, order, and catalog data across established telecom systems.</p>
                  </div>
                </div>
                <div className="ai-value-item" data-testid="ai-value-semantic-layer">
                  <span className="ai-value-number">02</span>
                  <div>
                    <h3>Semantic business layer</h3>
                    <p>Carrier-friendly semantic APIs and telecom ontology models that turn complex system language into business-ready capabilities.</p>
                  </div>
                </div>
                <div className="ai-value-item" data-testid="ai-value-market-launch">
                  <span className="ai-value-number">03</span>
                  <div>
                    <h3>Faster product launch</h3>
                    <p>Accelerated product configuration, offer readiness, impact analysis, and cross-system coordination for faster time-to-market.</p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="ai-capabilities-cta"
                onClick={() => scrollToSection('contact')}
                data-testid="ai-capabilities-contact-button"
              >
                Discuss AI roadmap <ChevronRight className="ml-2" size={18} />
              </button>
            </div>

            <div className="ai-capabilities-map" data-testid="ai-capabilities-map">
              <div className="ai-map-stage">
                <div className="ai-map-node node-bss" data-testid="ai-map-node-legacy-bss">
                  <span>Legacy BSS</span>
                  <strong>Billing • CRM • Catalog</strong>
                </div>
                <div className="ai-map-node node-ai" data-testid="ai-map-node-ai-layer">
                  <span>Zenom AI Layer</span>
                  <strong>Ontology • Semantics • Automation</strong>
                </div>
                <div className="ai-map-node node-market" data-testid="ai-map-node-market-launch">
                  <span>Market Launch</span>
                  <strong>Offers • Products • Revenue</strong>
                </div>
                <div className="ai-map-connector connector-one"></div>
                <div className="ai-map-connector connector-two"></div>
              </div>
              <div className="ai-map-metric" data-testid="ai-capabilities-metric">
                <span>Outcome focus</span>
                <strong>Shorter product cycles, clearer decisions, higher business agility</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="why-us-title">Why Choose ZenomTech</h2>
            <p className="section-subtitle">Two Decades of Telecom Excellence</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center" data-testid="why-experience">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-blue-600">20+</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Years Experience</h3>
              <p className="text-gray-600 text-sm">Two decades of expertise in Telecom BSS/OSS domain</p>
            </div>

            <div className="text-center" data-testid="why-global">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Global Footprint</h3>
              <p className="text-gray-600 text-sm">Serving clients across Americas, EMEA, APAC, and Caribbean</p>
            </div>

            <div className="text-center" data-testid="why-leadership">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Leadership</h3>
              <p className="text-gray-600 text-sm">Strong leadership with CXO engagement experience</p>
            </div>

            <div className="text-center" data-testid="why-clients">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Tier 1-2-3 Clients</h3>
              <p className="text-gray-600 text-sm">Trusted by major telecom operators worldwide</p>
            </div>
          </div>

          {/* Client Categories */}
          <div className="mt-16">
            <h3 className="text-center text-xl font-semibold text-gray-900 mb-8" data-testid="clients-title">Trusted by Industry Leaders</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 items-center">
              <div className="client-logo">Tier-1 Telecom Operators</div>
              <div className="client-logo">BSS/OSS Solution Providers</div>
              <div className="client-logo">Network Equipment Vendors</div>
              <div className="client-logo">MVNO Service Providers</div>
              <div className="client-logo">Cloud Infrastructure Partners</div>
              <div className="client-logo">Digital Experience Platforms</div>
              <div className="client-logo">Enterprise Technology Vendors</div>
              <div className="client-logo">Telecom System Integrators</div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer and Project Experience Section */}
      <section id="customer-experience" className="customer-experience-section section-padding" data-testid="customer-experience-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="customer-experience-header">
            <div>
              <div className="experience-eyebrow" data-testid="customer-experience-eyebrow">Customers & Project Experience</div>
              <h2 className="section-title" data-testid="customer-experience-title">Proven delivery across carriers and BSS/OSS vendors</h2>
            </div>
          </div>

          <div className="experience-metrics-grid" data-testid="customer-experience-metrics">
            <div className="experience-metric-card" data-testid="experience-metric-carriers">
              <strong>15+</strong>
              <span>Carriers Served</span>
            </div>
            <div className="experience-metric-card" data-testid="experience-metric-continents">
              <strong>4</strong>
              <span>Continents</span>
            </div>
            <div className="experience-metric-card" data-testid="experience-metric-years">
              <strong>20+</strong>
              <span>Years Delivering</span>
            </div>
            <div className="experience-metric-card" data-testid="experience-metric-vendors">
              <strong>Tier 1-3</strong>
              <span>Operator Experience</span>
            </div>
          </div>

          <div className="customer-logo-wall" data-testid="customer-logo-wall">
            <div className="customer-logo-wall-header">
              <h3 data-testid="customer-logo-wall-title">Representative customer and vendor experience</h3>
            </div>
            <div className="customer-logo-grid">
              <div className="customer-logo-card logo-tstt" data-testid="customer-logo-tstt">
                <span className="brand-symbol ring-symbol">T</span><span className="brand-wordmark">TSTT</span>
              </div>
              <div className="customer-logo-card logo-digicel" data-testid="customer-logo-digicel">
                <span className="brand-symbol dot-symbol">d</span><span className="brand-wordmark">digicel</span>
              </div>
              <div className="customer-logo-card logo-rogers" data-testid="customer-logo-rogers">
                <span className="brand-symbol circle-symbol"></span><span className="brand-wordmark">ROGERS</span>
              </div>
              <div className="customer-logo-card logo-telus" data-testid="customer-logo-telus">
                <span className="brand-symbol slash-symbol">✓</span><span className="brand-wordmark">TELUS</span>
              </div>
              <div className="customer-logo-card logo-o2" data-testid="customer-logo-o2">
                <span className="brand-symbol bubble-symbol">O₂</span><span className="brand-wordmark">O2</span>
              </div>
              <div className="customer-logo-card logo-america-movil" data-testid="customer-logo-america-movil">
                <span className="brand-symbol globe-symbol">AM</span><span className="brand-wordmark">América Móvil</span>
              </div>
              <div className="customer-logo-card logo-cellularone" data-testid="customer-logo-cellularone">
                <span className="brand-symbol signal-symbol">1</span><span className="brand-wordmark">CELLULARONE</span>
              </div>
              <div className="customer-logo-card logo-movil-exito" data-testid="customer-logo-movil-exito">
                <span className="brand-symbol star-symbol">★</span><span className="brand-wordmark">Móvil Éxito</span>
              </div>
              <div className="customer-logo-card logo-ericsson" data-testid="customer-logo-ericsson">
                <span className="brand-symbol bars-symbol"><i></i><i></i><i></i></span><span className="brand-wordmark">ERICSSON</span>
              </div>
              <div className="customer-logo-card logo-csg" data-testid="customer-logo-csg">
                <span className="brand-symbol hex-symbol">C</span><span className="brand-wordmark">CSG</span>
              </div>
              <div className="customer-logo-card logo-huawei" data-testid="customer-logo-huawei">
                <span className="brand-symbol petal-symbol">✦</span><span className="brand-wordmark">HUAWEI</span>
              </div>
              <div className="customer-logo-card logo-6d" data-testid="customer-logo-6d-technologies">
                <span className="brand-symbol cube-symbol">6D</span><span className="brand-wordmark">6D Technologies</span>
              </div>
              <div className="customer-logo-card logo-optiva" data-testid="customer-logo-optiva">
                <span className="brand-symbol wave-symbol">∿</span><span className="brand-wordmark">OPTIVA</span>
              </div>
              <div className="customer-logo-card logo-netcracker" data-testid="customer-logo-netcracker">
                <span className="brand-symbol grid-symbol">N</span><span className="brand-wordmark">NETCRACKER</span>
              </div>
              <div className="customer-logo-card logo-sunvizion" data-testid="customer-logo-sunvizion">
                <span className="brand-symbol sun-symbol">☼</span><span className="brand-wordmark">SunVizion</span>
              </div>
            </div>
          </div>

          <div className="project-experience-grid">
            <article className="project-experience-card" data-testid="project-experience-bss-transformation">
              <div className="project-experience-label">Carrier Transformation</div>
              <h3>BSS/OSS modernization and architecture delivery</h3>
              <p>Program leadership across billing, CRM, product catalog, order management, integrations, and TM Forum-aligned delivery models.</p>
            </article>
            <article className="project-experience-card" data-testid="project-experience-mvno-launch">
              <div className="project-experience-label">Launch & Migration</div>
              <h3>MVNO, MNO, and product launch enablement</h3>
              <p>Experience supporting subscriber migrations, new offer launches, platform rollouts, and cross-system readiness for market-facing teams.</p>
            </article>
            <article className="project-experience-card" data-testid="project-experience-vendor-delivery">
              <div className="project-experience-label">Vendor Ecosystem</div>
              <h3>BSS/OSS vendor and systems integration programs</h3>
              <p>Collaboration with telecom software vendors, system integrators, cloud partners, and enterprise platforms to deliver end-to-end outcomes.</p>
            </article>
            <article className="project-experience-card" data-testid="project-experience-ai-value">
              <div className="project-experience-label">AI & Data Value</div>
              <h3>Semantic APIs, ontology, analytics, and decision layers</h3>
              <p>Emerging AI-led tools that connect legacy BSS data with faster product configuration, better decisioning, and clearer business value.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="section-padding bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="case-studies-title">Success Stories</h2>
            <p className="section-subtitle">Delivering Excellence Across the Globe</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Case Study 1 */}
            <div className="case-study-card" data-testid="case-study-1">
              <div className="case-study-visual">
                <img src={TELECOM_TOWER_IMAGE} alt="Telecom infrastructure for BSS transformation" className="case-study-image" data-testid="case-study-1-image" />
                <div className="case-study-metric-overlay">
                  <div className="text-5xl font-bold mb-2">40%</div>
                  <div className="text-lg">Cost Reduction</div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Tier-1 Carrier BSS Transformation</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Led complete BSS/OSS modernization for a major Caribbean carrier, implementing TM Forum frameworks and reducing operational costs by 40%.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="tag">BSS/OSS</span>
                  <span className="tag">TM Forum</span>
                  <span className="tag">Caribbean</span>
                </div>
              </div>
            </div>

            {/* Case Study 2 */}
            <div className="case-study-card" data-testid="case-study-2">
              <div className="case-study-visual">
                <img src={DATA_CENTER_IMAGE} alt="Data center infrastructure for MVNO deployment" className="case-study-image" data-testid="case-study-2-image" />
                <div className="case-study-metric-overlay">
                  <div className="text-5xl font-bold mb-2">6M</div>
                  <div className="text-lg">Users Migrated</div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">MVNO Platform Deployment</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Successfully deployed and integrated MVNO platform for North American operator, seamlessly migrating 6 million subscribers with zero downtime.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="tag">MVNO</span>
                  <span className="tag">Migration</span>
                  <span className="tag">Americas</span>
                </div>
              </div>
            </div>

            {/* Case Study 3 */}
            <div className="case-study-card" data-testid="case-study-3">
              <div className="case-study-visual">
                <img src={NETWORK_DIAGRAM_IMAGE} alt="AI network graph for IoT and 5G use cases" className="case-study-image" data-testid="case-study-3-image" />
                <div className="case-study-metric-overlay">
                  <div className="text-5xl font-bold mb-2">3X</div>
                  <div className="text-lg">Revenue Growth</div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">IoT & 5G Use Case Implementation</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Designed and deployed enterprise IoT solutions leveraging 5G capabilities, resulting in 3x revenue growth for APAC operator&apos;s B2B segment.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="tag">5G</span>
                  <span className="tag">IoT</span>
                  <span className="tag">APAC</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insights Section */}
      <section id="insights" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="insights-title">Insights & Innovation</h2>
            <p className="section-subtitle">Thought Leadership in Telecom Technology</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Insight 1 */}
            <div className="insight-card" data-testid="insight-1">
              <img 
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab" 
                alt="5G Technology" 
                className="w-full h-48 object-cover rounded-t-lg"
              />
              <div className="p-6">
                <div className="text-blue-600 text-sm font-semibold mb-2">TELECOM TRENDS</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">The Future of 5G in Enterprise</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Exploring how 5G technology is transforming enterprise connectivity and enabling new IoT use cases.
                </p>
                <button className="text-blue-600 font-semibold text-sm flex items-center hover:text-blue-700" data-testid="insight-1-read-more-button">
                  Read More <ArrowRight size={16} className="ml-1" />
                </button>
              </div>
            </div>

            {/* Insight 2 */}
            <div className="insight-card" data-testid="insight-2">
              <img 
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa" 
                alt="BSS OSS" 
                className="w-full h-48 object-cover rounded-t-lg"
              />
              <div className="p-6">
                <div className="text-blue-600 text-sm font-semibold mb-2">BSS/OSS</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Modernizing BSS/OSS Architectures</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Best practices for transitioning from legacy systems to cloud-native BSS/OSS platforms.
                </p>
                <button className="text-blue-600 font-semibold text-sm flex items-center hover:text-blue-700" data-testid="insight-2-read-more-button">
                  Read More <ArrowRight size={16} className="ml-1" />
                </button>
              </div>
            </div>

            {/* Insight 3 */}
            <div className="insight-card" data-testid="insight-3">
              <img 
                src="https://images.unsplash.com/photo-1580982330720-bd5e0fed108b" 
                alt="TM Forum" 
                className="w-full h-48 object-cover rounded-t-lg"
              />
              <div className="p-6">
                <div className="text-blue-600 text-sm font-semibold mb-2">DIGITAL TRANSFORMATION</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">TM Forum Open APIs in Action</h3>
                <p className="text-gray-600 text-sm mb-4">
                  How TM Forum Open APIs accelerate digital transformation and enable ecosystem collaboration.
                </p>
                <button className="text-blue-600 font-semibold text-sm flex items-center hover:text-blue-700" data-testid="insight-3-read-more-button">
                  Read More <ArrowRight size={16} className="ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="contact-title">Get In Touch</h2>
            <p className="section-subtitle">Let&apos;s discuss your next project</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="form-label">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="form-input"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="form-input"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    className="form-input"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="form-label">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleFormChange}
                    className="form-input"
                    data-testid="contact-company-input"
                  />
                </div>

                <div>
                  <label htmlFor="service_interest" className="form-label">Service Interest</label>
                  <select
                    id="service_interest"
                    name="service_interest"
                    value={formData.service_interest}
                    onChange={handleFormChange}
                    className="form-input"
                    data-testid="contact-service-select"
                  >
                    <option value="">Select a service...</option>
                    <option value="BSS/OSS Transformation">BSS/OSS Transformation</option>
                    <option value="Data Analytics">Data Analytics</option>
                    <option value="Enterprise IT Services & TM Forum">Enterprise IT Services & TM Forum</option>
                    <option value="Cloud Solutions">Cloud Solutions</option>
                    <option value="System Integration">System Integration</option>
                    <option value="AI Technology Solutions">AI Technology Solutions</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    required
                    rows={4}
                    className="form-input"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-button"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      <Send size={20} className="mr-2" />
                      Send Message
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="success-message" data-testid="contact-success-message">
                    <CheckCircle size={20} className="mr-2" />
                    Thank you! We&apos;ll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="error-message" data-testid="contact-error-message">
                    Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Contact Information</h3>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-phone-info">
                  <Phone className="text-blue-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-gray-900">Phone</div>
                    <div className="text-gray-600">+1 (647) 609-4204</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-email-info">
                  <Mail className="text-blue-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-gray-900">Email</div>
                    <div className="text-gray-600">info@zenomtech.com</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-location-info">
                  <MapPin className="text-blue-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-gray-900">Global Presence</div>
                    <div className="text-gray-600">Americas • EMEA • APAC • Caribbean</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-testid="contact-linkedin-info">
                  <Linkedin className="text-blue-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-gray-900">LinkedIn</div>
                    <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700" data-testid="contact-linkedin-link">Follow us on LinkedIn</a>
                  </div>
                </div>

                {/* Newsletter Signup */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-4">Subscribe to Our Newsletter</h4>
                  <form onSubmit={handleNewsletterSubmit} className="flex gap-2" data-testid="newsletter-form">
                    <input
                      type="email"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      placeholder="Your email"
                      required
                      className="form-input flex-1"
                      data-testid="newsletter-email-input"
                    />
                    <button type="submit" className="newsletter-button" data-testid="newsletter-submit-button">
                      Subscribe
                    </button>
                  </form>
                  {newsletterStatus === 'success' && (
                    <div className="text-green-600 text-sm mt-2" data-testid="newsletter-success-message">
                      ✓ Successfully subscribed!
                    </div>
                  )}
                  {newsletterStatus === 'error' && (
                    <div className="text-red-600 text-sm mt-2" data-testid="newsletter-error-message">
                      Email already subscribed or error occurred.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://static.wixstatic.com/media/022cd5_82670ae94fbc4e3b809e81767dc7ea23~mv2.jpg/v1/fill/w_210,h_210,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-04-02%20at%2018_50_edited.jpg" 
                  alt="ZenomTech" 
                  className="h-10 w-10 rounded-full"
                />
                <span className="ml-3 text-xl font-bold">ZenomTech</span>
              </div>
              <p className="text-gray-400 text-sm">Your IT/Telecom Solutions Experts</p>
              <p className="text-gray-400 text-sm mt-2">Redefining BSS/OSS for 20+ years</p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-white" data-testid="footer-nav-home-button">Home</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white" data-testid="footer-nav-services-button">Services</button></li>
                <li><button onClick={() => scrollToSection('ai-capabilities')} className="text-gray-400 hover:text-white" data-testid="footer-nav-ai-capabilities-button">AI Capabilities</button></li>
                <li><button onClick={() => scrollToSection('why-us')} className="text-gray-400 hover:text-white" data-testid="footer-nav-why-us-button">Why Us</button></li>
                <li><button onClick={() => scrollToSection('customer-experience')} className="text-gray-400 hover:text-white" data-testid="footer-nav-customer-experience-button">Experience</button></li>
                <li><button onClick={() => scrollToSection('case-studies')} className="text-gray-400 hover:text-white" data-testid="footer-nav-case-studies-button">Case Studies</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Services</h3>
              <ul className="space-y-2 text-sm">
                <li className="text-gray-400">BSS/OSS Transformation</li>
                <li className="text-gray-400">Data Analytics</li>
                <li className="text-gray-400">Cloud Solutions</li>
                <li className="text-gray-400">System Integration</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>+1 (647) 609-4204</li>
                <li>info@zenomtech.com</li>
                <li className="mt-4">
                  <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:text-white" data-testid="footer-linkedin-link">
                    <Linkedin size={20} className="mr-2" /> LinkedIn
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 ZenomTech. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <LiveChatWidget apiBaseUrl={API} />
    </div>
  );
}

export default App;